import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Alert, Spinner } from "react-bootstrap";
import { FaTrashAlt } from "react-icons/fa";
import { adminContactMessageAPI } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import PaginationComponent from "../common/PaginationComponent";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
// import "../assets/css/messages.css";

function Contact() {
  const { t } = useTranslation();
  const { isSuperAdmin, checkSuperAdminStatus } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const fetchMessages = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      const response = await adminContactMessageAPI.index({
        page,
        per_page: pagination.per_page,
      });

      if (response.success) {
        setData(response.data.data || []);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data.length,
        });
      } else {
        setError(response.message || t("failed_to_fetch_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_fetch_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_fetch_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_fetch_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    checkSuperAdminStatus();
  }, []);

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminContactMessageAPI.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("data_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDelete(false);
        fetchMessages(pagination.current_page);
      } else {
        setError(response.message || t("failed_to_delete_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_delete_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_delete_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_delete_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting message:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="dashboard-header">
        <h4 className="mb-0">{t("Contact Messages")}</h4>
      </div>
      <div className="py-4">
        <h5 className="mb-4 dashboard-title">{t("Contact Messages")}</h5>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : data.length === 0 ? (
          <Alert variant="info">{t("no_data_available")}</Alert>
        ) : (
          <div className="table-container shadow-sm rounded">
            <div className="table-responsive">
              <Table bordered hover className="custom-table mb-0 text-center">
                <thead className="table-dark">
                  <tr>
                    <th>{t("id")}</th>
                    <th>{t("name")}</th>
                    <th>{t("email")}</th>
                    <th>{t("phone")}</th>
                    <th>{t("message")}</th>
                    <th>{t("created_at")}</th>
                    {isSuperAdmin && <th>{t("action")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.id}</td>
                      <td>{entry.name|| "N/A"}</td>
                      <td>{entry.email || "N/A"}</td>
                      <td>{entry.phone}</td>
                      <td>{entry.message}</td>
                      {/* <td>{new Date(entry.created_at).toLocaleString()}</td> */}
                      <td dir="rtl">
                        {entry?.created_at
                          ? new Date(entry.created_at).toLocaleString()
                          : '-'}
                      </td>
                      {isSuperAdmin && (
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setDeleteId(entry.id);
                              setShowDelete(true);
                            }}
                            title={t("delete")}
                            disabled={loading}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {!loading && pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchMessages}
            disabled={loading}
          />
        )}
      </div>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_message")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t("deleting") : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Contact;